import { useCallback, useEffect, useRef, useState } from 'react';

import { formatoDeVideo } from '@haus/core';

/**
 * Grabación de un clip por estancia.
 *
 * Clips cortos y no un vídeo único, y esto no es una preferencia: **Safari en
 * iOS corta las grabaciones largas cuando la app pasa a segundo plano**. Un
 * vídeo de 40 minutos de una limpieza entera es exactamente el caso que se
 * pierde. Trocear por estancia es además lo que hace viable subir: cada clip
 * sale hacia el NAS mientras se graba el siguiente.
 *
 * El tope de 90 s y el mínimo de 15 vienen del handoff y los trae cada estancia
 * desde su plantilla, así que se pasan como parámetros.
 */

export type EstadoGrabacion = 'parada' | 'grabando' | 'procesando';

export interface Grabacion {
    estado: EstadoGrabacion;
    segundos: number;
    soportado: boolean;
    mime: string | null;
    empezar: () => void;
    parar: () => Promise<Blob | null>;
    descartar: () => void;
}

export function useGrabacion(stream: MediaStream | null, maxSegundos = 90): Grabacion {
    const [estado, setEstado] = useState<EstadoGrabacion>('parada');
    const [segundos, setSegundos] = useState(0);
    const grabadora = useRef<MediaRecorder | null>(null);
    const trozos = useRef<Blob[]>([]);
    const intervalo = useRef<number | null>(null);
    const mime = formatoDeVideo();

    const limpiar = useCallback(() => {
        if (intervalo.current !== null) {
            clearInterval(intervalo.current);
            intervalo.current = null;
        }
    }, []);

    const empezar = useCallback(() => {
        if (stream === null || mime === null || grabadora.current !== null) {
            return;
        }

        trozos.current = [];
        const g = new MediaRecorder(stream, {
            mimeType: mime,
            // 2,5 Mbps a 720p: suficiente para que se vea si un cristal está
            // limpio y razonable para 180 MB por limpieza. Subirlo llena el
            // NAS y la línea de la oficina; bajarlo hace la prueba discutible.
            videoBitsPerSecond: 2_500_000,
            audioBitsPerSecond: 64_000,
        });

        g.ondataavailable = (e) => {
            if (e.data.size > 0) {
                trozos.current.push(e.data);
            }
        };

        // Un trozo por segundo. En iOS, si se pide el blob entero al final y
        // la pestaña se suspende a mitad, se pierde todo; con trozos, lo
        // grabado hasta ese momento está en memoria y se puede cerrar.
        g.start(1000);
        grabadora.current = g;
        setEstado('grabando');
        setSegundos(0);

        intervalo.current = setInterval(() => {
            setSegundos((s) => s + 1);
        }, 1000) as unknown as number;
    }, [stream, mime]);

    const parar = useCallback(async () => {
        const g = grabadora.current;
        if (g === null) {
            return null;
        }

        limpiar();
        setEstado('procesando');

        const blob = await new Promise<Blob>((resolver) => {
            g.onstop = () => {
                resolver(new Blob(trozos.current, { type: mime ?? 'video/mp4' }));
            };
            if (g.state !== 'inactive') {
                g.stop();
            } else {
                resolver(new Blob(trozos.current, { type: mime ?? 'video/mp4' }));
            }
        });

        grabadora.current = null;
        setEstado('parada');

        return blob.size === 0 ? null : blob;
    }, [limpiar, mime]);

    const descartar = useCallback(() => {
        limpiar();
        const g = grabadora.current;
        if (g !== null && g.state !== 'inactive') {
            g.onstop = null;
            g.stop();
        }
        grabadora.current = null;
        trozos.current = [];
        setEstado('parada');
        setSegundos(0);
    }, [limpiar]);

    // Corte automático al llegar al máximo. Sin esto, un móvil olvidado en el
    // bolsillo graba hasta llenar el almacenamiento.
    useEffect(() => {
        if (estado === 'grabando' && segundos >= maxSegundos) {
            void parar();
        }
    }, [estado, segundos, maxSegundos, parar]);

    useEffect(() => descartar, [descartar]);

    return {
        estado,
        segundos,
        soportado: mime !== null,
        mime,
        empezar,
        parar,
        descartar,
    };
}
